import Downtime from '@components/layout/downtime';
import type { Integration } from '@interfaces/integrations';
import {
  IntegrationProviders,
  IntegrationState,
  IntegrationTypes,
} from '@interfaces/integrations';
import { PageConfigurationTypes } from '@interfaces/pageConfiguration';
import { NavigationStyles } from '@interfaces/role';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { AppBar, Toolbar } from '@ovComponents/2-component/appBar/appBar';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { AppBreadcrumbs } from '@ovComponents/3-pattern/appBreadcrumbs/appBreadcrumbs';
import EntityMenu from '@ovComponents/3-pattern/entityMenu/entityMenu';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useAuthContext } from '@providers/ovApolloProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import {
  UserContext,
  useFeatureFlags,
  usePermissions,
} from '@providers/userContextProvider';
import { entityName } from '@utils/common';
import { FlagsmithFlags } from '@utils/flagsmith';
import { isUndefined } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Notifications } from '../notifications/notifications';
import { getZendeskKeys, ZendeskWidget } from '../widgets/chatAgent/zendesk';
import AlertBanner from './alertBanner';
import Disclaimer from './disclaimer';
import { useLayoutTokens } from './layout.tokens';
import OrganizationLogo from './organizationLogo';
import Sidebar from './sidebar';

const pageTypeMap: Record<string, PageConfigurationTypes> = {
  clients: PageConfigurationTypes.INDIVIDUAL,
  nonIndividualClients: PageConfigurationTypes.NON_INDIVIDUAL,
  households: PageConfigurationTypes.HOUSEHOLD,
  household: PageConfigurationTypes.HOUSEHOLD,
};

const pageTypes = [
  PageConfigurationTypes.INDIVIDUAL,
  PageConfigurationTypes.NON_INDIVIDUAL,
  PageConfigurationTypes.HOUSEHOLD,
];

const ALERT_BANNER_IGNORE_PATHS: readonly string[] = [
  'settings',
  'reconciliation',
  'orgSettings',
];

// read query params once per path (memoized) to avoid drift across effects
const useQueryParams = () =>
  useMemo(() => new URLSearchParams(window.location.search), []);

const Layout = () => {
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const {
    userContext,
    displayMenuBar,
    closed,
    integrations,
    // ^^ is needed to conditionally load 3rd party integration scripts
    // e.g for Zendesk and Fraud Detection (Simility)
    applicableDowntimeSchedule,
  } = useContext(UserContext);
  const { permissions } = usePermissions();
  const { flagEnabled } = useFeatureFlags();
  const { appLogout } = useAuthContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { sys, comp } = useThemeTokens(useLayoutTokens());

  const logout = async () => {
    await appLogout();
  };

  // This it to make pages scroll to top after every navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // read query once to prevent subtle differences
  const query = useQueryParams();

  // This it to redirect to root path on downtime
  useEffect(() => {
    if (applicableDowntimeSchedule) navigate('/');
  }, [navigate, applicableDowntimeSchedule]);

  const splitPath = pathname.split('/').filter(Boolean);
  const pageTypeFromPath = pageTypeMap[splitPath[splitPath.length - 2]];
  const hasNotifications = permissions.includes('read:notification');
  const isUnifiedClientExperience =
    userContext.role?.navigationStyle === NavigationStyles.SIMPLE;
  const appBarMobileAndTabletColor = comp.layout.backgroundColor;
  const appBarDesktopColor = comp.layout.backgroundColor;
  const hideAppBarActions = !!applicableDowntimeSchedule;

  // conditionally load the Simility fraud detection scripts
  useEffect(() => {
    const conditionallyInjectSimilityScripts = (
      orgIntegrations: Integration[],
    ) => {
      const config = orgIntegrations.find(
        ({ type, provider, state }) =>
          type === IntegrationTypes.FRAUD_DETECTION &&
          provider === IntegrationProviders.SIMILITY &&
          state === IntegrationState.ACTIVE,
      )?.configuration;

      if (
        !config ||
        !config.pageKeys ||
        !config.fnclsValue ||
        !config.scriptInjectURL
      )
        return;

      const CMID = window.sessionStorage.getItem('CMID');
      if (!CMID) {
        console.warn(
          'Missing CMID. Will abort Simility fraud management initialization for ORG client!',
        );
        return;
      }

      const { scriptInjectURL, fnclsValue, pageKeys = '' } = config;
      const pageKeysList = pageKeys.split(/\s*,\s*/);

      const page = pageKeysList.find((entry: string) => {
        const match = pathname.match(/\/+([a-zA-Z]+)\/?.*/);
        return match?.at(1) === entry;
      });

      if (!page) return;

      // add/update script data values for this path
      let dataIslandScript = document.querySelector('script[fncls]');
      if (!dataIslandScript) {
        dataIslandScript = document.createElement('script');
        dataIslandScript.setAttribute('type', 'application/json');
        dataIslandScript.setAttribute('fncls', fnclsValue);
        try {
          document.body.appendChild(dataIslandScript);
        } catch (err) {
          console.error('Error injecting fraud detection data script:', err);
        }
      }

      dataIslandScript.textContent = JSON.stringify({
        f: CMID,
        s: page.toUpperCase(),
      });

      // add Simility script
      let fraudnetInitScript = document.querySelector('#fraudnet-init');
      if (!fraudnetInitScript) {
        fraudnetInitScript = document.createElement('script');
        fraudnetInitScript.setAttribute('id', 'fraudnet-init');
        fraudnetInitScript.setAttribute('src', scriptInjectURL);
        try {
          document.body.appendChild(fraudnetInitScript);
        } catch (err) {
          console.error('Error injecting fraud detection init script:', err);
        }
      }
    };

    if (userContext) {
      conditionallyInjectSimilityScripts(integrations);
    }
  }, [pathname, userContext, integrations]);

  // handle `custodianmeta` redirects into OV — currently only the credentials
  // manager success toast via traceId handshake. The logout-on-redirect cases
  // (status=LOGOUT, loggedOutMessage=inactive|changeusername) are handled in an
  // earlier layer in Auth0Provider's mount-effect pre-flight. (OVT-5794)
  useEffect(() => {
    const custodianMeta = query.get('custodianmeta') || '';
    if (!custodianMeta) return;

    const traceId = custodianMeta.match(/traceId:([^&]+)/)?.[1];
    if (!traceId) return;

    const storedTraceId = window.localStorage.getItem(
      'custodianCredentialsManagerTraceId',
    );
    if (traceId !== storedTraceId) return;

    window.localStorage.removeItem('custodianCredentialsManagerTraceId');
    // UX: this is the "settings updated" case. Can be updated when other cases arise
    showToast({
      message: 'Settings updated successfully',
      severity: 'success',
    });
  }, []);

  const pageOptions = () => {
    switch (pageTypeFromPath) {
      case PageConfigurationTypes.INDIVIDUAL:
        return userContext?.role?.individualPageConfiguration?.options;
      case PageConfigurationTypes.NON_INDIVIDUAL:
        return userContext?.role?.nonIndividualPageConfiguration?.options;
      case PageConfigurationTypes.HOUSEHOLD:
        return userContext?.role?.householdPageConfiguration?.options;
      default:
        return undefined;
    }
  };

  const options = pageOptions();

  return (
    <Box sx={{ display: 'flex' }}>
      {displayMenuBar && (
        <AppBar
          position='fixed'
          elevation={0}
          sx={{
            width: { md: '100%' },
            transition: 'all 0.3s',
            backgroundColor: {
              xs: appBarMobileAndTabletColor,
              md: appBarDesktopColor,
            },
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box display='flex' flexDirection='row' alignItems='center'>
              {!hideAppBarActions && (
                <IconButton
                  onClick={() => setMobileOpen(!mobileOpen)}
                  size='small'
                  sx={{
                    color: comp.layout.textColor,
                    mr: 1,
                    display: {
                      xs: !isUnifiedClientExperience ? 'block' : 'none',
                      lg: 'none',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <OrganizationLogo textColor={comp.layout.textColor} />
            </Box>
            <Box display='flex' justifyContent='end' alignItems='center'>
              {hasNotifications && !hideAppBarActions && (
                <Notifications color={comp.layout.textColor} />
              )}
              <EntityMenu logout={logout} showOnlyLogout={hideAppBarActions} />
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: '100%', width: '1vw' },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' },
          bgcolor: sys.color.background,
          mt: isUnifiedClientExperience && !displayMenuBar ? 0 : 7,
        }}
      >
        {applicableDowntimeSchedule ? (
          <>
            {!displayMenuBar && (
              <Box
                display='flex'
                flexDirection='column'
                alignContent={isUnifiedClientExperience ? 'center' : 'start'}
                flexWrap='wrap'
              >
                <Box
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  maxWidth='1080px'
                  width='100%'
                  height='32px'
                  mb={2}
                  ml={{ xs: 0, sm: 2 }}
                >
                  <Box
                    display='flex'
                    justifyContent='end'
                    alignItems='center'
                    marginLeft='auto'
                  >
                    <EntityMenu logout={logout} showOnlyLogout />
                  </Box>
                </Box>
              </Box>
            )}
            <Downtime downtime={applicableDowntimeSchedule} />
          </>
        ) : (
          <Box
            justifyContent='start'
            alignItems='center'
            sx={{ height: '100%' }}
            display='flex'
          >
            {!isUnifiedClientExperience && (
              <Sidebar
                drawerWidth={closed ? 48 : 240}
                setMobileOpen={setMobileOpen}
                mobileOpen={mobileOpen}
              />
            )}
            <Box
              width='100%'
              sx={{
                transition: 'all 0.4s',
                maxWidth: {
                  lg: `calc(100% - ${!isUnifiedClientExperience ? `${closed ? 48 : 240}px` : '0px'})`,
                },
                ml: {
                  xs: '0px',
                  sm: '0px',
                  md: '0px',
                  lg: !isUnifiedClientExperience
                    ? `${closed ? 48 : 240}px`
                    : '0px',
                },
              }}
            >
              {flagEnabled(FlagsmithFlags.alert_banners) &&
                !ALERT_BANNER_IGNORE_PATHS.includes(splitPath[0]) && (
                  <AlertBanner />
                )}
              {pageTypes.includes(pageTypeFromPath) &&
                options?.enableContent && (
                  <Disclaimer
                    options={options}
                    sx={{
                      maxWidth: !isUnifiedClientExperience
                        ? `${options?.maxPageWidth || 1080}px`
                        : '100%',
                    }}
                  />
                )}
              {/* Note: The breadcrumb for "UnifiedClientExperience" when "displayMenuBar" is set to true is displayed on pages such as viewPageVisual */}
              {!isUnifiedClientExperience && displayMenuBar && (
                <Box mb={3} mt={1}>
                  <AppBreadcrumbs />
                </Box>
              )}
              {!displayMenuBar && (
                <Box
                  display='flex'
                  flexDirection='column'
                  alignContent={isUnifiedClientExperience ? 'center' : 'start'}
                  flexWrap='wrap'
                >
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    maxWidth='1080px'
                    width='100%'
                    height='32px'
                    mb={2}
                    ml={{ xs: 0, sm: 2 }}
                  >
                    <AppBreadcrumbs />
                    <Box
                      display='flex'
                      justifyContent='end'
                      alignItems='center'
                      marginLeft='auto'
                    >
                      {hasNotifications && (
                        <Notifications color={comp.layout.textColor} />
                      )}
                      <EntityMenu logout={logout} />
                    </Box>
                  </Box>
                </Box>
              )}
              {/* This is where actual component gets rendered (see appRoutes.tsx) */}
              <Outlet />
              {/* Temporary Zendesk workaround. Refactor when adding new chat providers. */}
              {userContext?.accessType &&
                !isUndefined(getZendeskKeys(integrations)) && (
                  <ZendeskWidget
                    widgetStyle={{ color: sys.color.primary }}
                    accessType={userContext?.accessType}
                    user={entityName(userContext)}
                    scriptKeys={getZendeskKeys(integrations)}
                  />
                )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Layout;
